/**
 * Lazy Load Images
 * Modern implementation using Intersection Observer API.
 * Replaces jQuery-based gdunveil plugin.
 */

/**
 * Initialize lazy loading for images with .geodir-lazy-load class.
 * Uses data-src and data-srcset attributes.
 *
 * @returns {Function} Cleanup function to disconnect observer
 */
export function initLazyLoad() {
	const images = document.querySelectorAll('.geodir-lazy-load');

	if (images.length === 0) {
		return () => {};
	}

	// Check if browser supports Intersection Observer
	if (!('IntersectionObserver' in window)) {
		// Fallback: load all images immediately
		images.forEach(img => loadImage(img));
		return () => {};
	}

	// Determine if we should use retina images
	const useRetina = window.devicePixelRatio > 1;
	const threshold = 100; // pixels

	// Create intersection observer with root margin for preloading
	const observer = new IntersectionObserver((entries) => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				const img = entry.target;
				loadImage(img, useRetina);
				observer.unobserve(img);
			}
		});
	}, {
		rootMargin: `${threshold}px`
	});

	// Observe all lazy load images
	images.forEach(img => observer.observe(img));

	// Return cleanup function
	return () => observer.disconnect();
}

/**
 * Load an image by setting src/srcset from data attributes.
 *
 * @param {HTMLImageElement} img - Image element to load
 * @param {boolean} useRetina - Whether to prefer retina source
 */
function loadImage(img, useRetina = false) {
	// Get the appropriate source
	let source = null;

	if (useRetina && img.hasAttribute('data-src-retina')) {
		source = img.getAttribute('data-src-retina');
	}

	if (!source && img.hasAttribute('data-src')) {
		source = img.getAttribute('data-src');
	}

	// Get srcset if available
	const srcset = img.getAttribute('data-srcset');

	// Set the sources
	if (srcset) {
		img.setAttribute('srcset', srcset);
	}

	if (source) {
		img.setAttribute('src', source);
	}

	// Remove lazy load class
	img.classList.remove('geodir-lazy-load');

	// Set opacity (for browsers that support objectFit, which is all modern browsers)
	img.style.opacity = '1';

	// Dispatch custom event
	img.dispatchEvent(new CustomEvent('geodir:lazyLoaded', { bubbles: true }));
}

/**
 * Trigger lazy load check manually.
 * Useful after tab switches or content reveals.
 */
export function triggerLazyLoadCheck() {
	// Dispatch scroll event to trigger intersection checks
	window.dispatchEvent(new Event('scroll'));
}
